// Toggle.tsx
import React, { FunctionComponent } from "react";
import styled from "styled-components";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import Colors from "../global/Colors";
import Fonts from "../global/Fonts";

const StyledSwitch = styled(Switch)<{ $colorMode?: 'light' | 'dark', $labelPlacement?: 'start' | 'end' }>`
  && {
    margin: ${props => props.$labelPlacement === 'end' ? '0 0 0 10px !important' : '0 10px 0 0 !important'};
    
    .MuiSwitch-switchBase {
      color: ${props => props.$colorMode === 'dark' ? Colors.grey3 : Colors.grey6};
      
      &.Mui-checked {
        color: ${props => props.$colorMode === 'dark' ? Colors.white : Colors.grey7};
        
        & + .MuiSwitch-track {
          background-color: ${props => props.$colorMode === 'dark' ? Colors.grey5 : Colors.grey4};
          opacity: 1;
        }
      }

      &:hover {
        background-color: ${props => props.$colorMode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)'};
      }
    }
    
    .MuiSwitch-track {
      background-color: ${props => props.$colorMode === 'dark' ? Colors.grey7 : Colors.grey2};
      opacity: 1;
    }

    &.Mui-disabled {
      .MuiSwitch-switchBase {
        color: ${Colors.grey5};
      }
      .MuiSwitch-track {
        background-color: ${props => props.$colorMode === 'dark' ? Colors.grey7 : Colors.grey2};
        opacity: 0.5;
      }
    }
  }
`;

const StyledLabel = styled.span<{ $colorMode?: 'light' | 'dark' }>`
  font-family: ${Fonts.lexendMedium.fontFamily};
  font-weight: ${Fonts.lexendMedium.fontWeight};
  font-size: 16px;
  color: ${props => props.$colorMode === 'dark' ? Colors.white : Colors.black};
`;

export interface ToggleProps {
  checked: boolean;
  label?: { text: string; placement: 'start' | 'end' };
  colorMode?: 'dark' | 'light';
  disabled?: boolean;
  size?: 'small' | 'medium';
  onChange: (checked: boolean) => void;
}

const Toggle: FunctionComponent<ToggleProps> = ({
  checked,
  onChange,
  label,
  colorMode = 'light',
  disabled = false,
  size = 'medium',
}) => {

  const switch_component = (
    <StyledSwitch
      checked={checked}
      onChange={(event) => onChange(event.target.checked)}
      $colorMode={colorMode}
      $labelPlacement={label?.placement ?? 'end'}
      disabled={disabled}
      size={size}
    />
  );

  if (!label) {
    return switch_component;
  }

  return (
    <FormControlLabel
      control={switch_component}
      label={<StyledLabel $colorMode={colorMode}>{label.text}</StyledLabel>}
      labelPlacement={label?.placement}
    />
  );
};

export default Toggle;